import React, { useState, useEffect } from 'react';

const EcosystemSection = () => {
  const [stats, setStats] = useState({
    projects: 0,
    clients: 0,
    years: 0,
    members: 0
  });
  const [expandedImage, setExpandedImage] = useState(null);

  useEffect(() => {
    const animateCounter = (target, duration = 2000) => {
      let current = 0;
      const increment = target / (duration / 16);
      
      const interval = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(interval);
        }
      }, 16);
      
      return interval;
    };

    // Trigger animations when component mounts
    const timer = setTimeout(() => {
      setStats({
        projects: 15,
        clients: 20,
        years: 6,
        members: 12
      });
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const statCards = [
    {
      label: 'Projects Delivered',
      value: stats.projects,
      target: 15,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      label: 'Active Clients',
      value: stats.clients,
      target: 20,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      label: 'Months Proven',
      value: stats.years,
      target: 6,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      label: 'Expert Members',
      value: stats.members,
      target: 12,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      )
    }
  ];

  const images = [
    {
      src: '/img/DSC_0849.jpg',
      alt: 'Garud Stacks enterprise team working together',
      colSpan: 'col-span-2',
      rowSpan: 'row-span-2'
    },
    {
      src: '/img/coding.png',
      alt: 'Coding',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      src: '/img/sa1.jpg',
      alt: 'Agreement',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      src: '/img/dharmendra.jpeg',
      alt: 'Team Member',
      colSpan: 'col-span-1',
      rowSpan: 'row-span-1'
    },
    {
      src: '../img/small.png',
      alt: 'Office Space',
      colSpan: 'col-span-2',
      rowSpan: 'row-span-1'
    }
  ];

  return (
    <section className="relative bg-gray-50 py-20 lg:py-32 overflow-hidden border-b border-gray-200">
      {/* Background Glows */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[120px] pointer-events-none z-0"></div>
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-[100px] pointer-events-none z-0"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10 grid lg:grid-cols-2 gap-16 items-center">
        
        {/* Left Content: Text & Stats */}
        <div>
          {/* Badge */}
          <div className="inline-flex items-center border border-gray-200 px-4 py-2 bg-white/80 backdrop-blur-sm text-xs font-bold uppercase tracking-widest text-amber-600 mb-6 rounded-md shadow-sm">
            <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
            Our Ecosystem
          </div>
          
          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight uppercase mb-6 text-gray-900">
            Building Enterprise <br />
            <span className="text-amber-600">Solutions</span>
          </h2>
          
          {/* Description */}
          <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-12 max-w-xl">
            Garud Stacks builds enterprise-grade technology solutions delivering measurable business outcomes. From strategy to deployment, we drive your digital transformation with proven expertise, our work reflects experience, execution, and measurable impact.
          </p>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-6 mb-10">
            {statCards.map((stat, index) => (
              <div 
                key={index}
                className="bg-gray-900 border border-gray-800 p-6 rounded-2xl shadow-xl hover:shadow-2xl hover:border-amber-500/50 hover:-translate-y-2 transition-all duration-400 group relative overflow-hidden"
              >
                {/* Background Glow */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:bg-amber-500/20 transition-all"></div>
                
                {/* Content */}
                <div className="flex justify-between items-start mb-2 relative z-10">
                  <strong className="block text-4xl lg:text-5xl font-extrabold text-amber-500">
                    {Math.round(stat.value)}+
                  </strong>
                  <div className="text-gray-600 group-hover:text-amber-400 transition-colors">
                    {stat.icon}
                  </div>
                </div>
                
                {/* Label */}
                <span className="text-sm font-bold text-gray-300 uppercase tracking-wide relative z-10">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
          
          {/* CTA Button */}
          <a 
            href="/about"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-bold uppercase tracking-widest px-8 py-4 rounded-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
          >
            View Our Journey
            <span>→</span>
          </a>
        </div>
        
        {/* Right Content: Image Bento Grid */}
        <div className="grid grid-cols-3 grid-rows-3 gap-3 lg:gap-4 h-[500px] lg:h-[600px]">
          {images.map((image, index) => (
            <div 
              key={index}
              className={`${image.colSpan} ${image.rowSpan} rounded-2xl lg:rounded-3xl overflow-hidden shadow-lg group relative cursor-pointer`}
              onClick={() => setExpandedImage(image.src)}
            >
              <div className="absolute inset-0 bg-gray-900/10 group-hover:bg-transparent transition-colors duration-500 z-10 pointer-events-none"></div>
              <img 
                src={image.src} 
                alt={image.alt} 
                className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110 cursor-pointer"
              />
            </div>
          ))}
        </div>

        {/* Expanded Image Modal */}
        {expandedImage && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setExpandedImage(null)}
          >
            <div 
              className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={expandedImage}
                alt="Expanded view"
                className="w-full h-full object-contain"
              />
              <button
                onClick={() => setExpandedImage(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors duration-300 backdrop-blur-sm"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        )}
        
      </div>
    </section>
  );
};

export default EcosystemSection;
